import { AppUser } from 'Src/views/App/App';
import axios from 'Src/helpers/api/axiosInstance';
import { Permission } from 'Src/models/permission';
import { Count } from '../../../models/count';
import { fetchAllSearchPages } from './fetchAllSearchPages';
import { normalizeSchoolUserContactSearchWhere } from './schoolUserContactSearch';

const DEFAULT_STAFFS_FILTER: any = {
  'permissions.preset': { $in: ['ADMIN', 'MANAGER', 'COACH', 'TEACHER', 'SLT'] }
};

export interface CreateSchoolStaffInput {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  preset: string;
  sltAccess?: {studentDrilldown: boolean};
}

export interface CreatedSchoolStaff {
  id: string;
  firstName: string;
  lastName: string;
  preset: string;
  channelsConfigured: boolean;
}

export function createSchoolStaff(user: AppUser, input: CreateSchoolStaffInput): Promise<CreatedSchoolStaff> {
  return axios.post(`${window.apiBase}/i/schools/${user.activeSchoolId}/users/staff`, input,
    {headers: {usid: user.roleSessionKey}}).then(response => response.data);
}

/** STAFF TABLE */
export function getSchoolStaffs(user: AppUser, filter = DEFAULT_STAFFS_FILTER) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          ...DEFAULT_STAFFS_FILTER,
          ...filter.where
        },
        skip: filter.skip,
        limit: filter.limit,
        order: filter.order
      }
    }
  };

  config.params.filter.where = normalizeSchoolUserContactSearchWhere(config.params.filter.where);

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/users`, config).then(response => {
    return response.data;
  });
}

export function searchSchoolStaffs(user: AppUser, filter): Promise<{ items: any[]; total: number }> {
  const { roleSessionKey: key, activeSchoolId } = user;
  const requestFilter = {
    ...filter,
    where: normalizeSchoolUserContactSearchWhere({ ...DEFAULT_STAFFS_FILTER, ...(filter.where || {}) })
  };
  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/users/search`, requestFilter, { headers: { usid: key } })
    .then(response => response.data);
}

export function getSchoolStaffsCount(user: AppUser, where?): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_STAFFS_FILTER,
            ...where
          }
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_STAFFS_FILTER
          }
        }
      }
    };
  }

  config.params.filter.where = normalizeSchoolUserContactSearchWhere(config.params.filter.where);

  return axios.get<Count>(`${window.apiBase}/i/schools/${activeSchoolId}/users/count`, config).then(response => {
    return response.data;
  });
}

export function getAllSchoolStaffs(user: AppUser, where?) {
  return fetchAllSearchPages(filter => searchSchoolStaffs(user, filter), where || {});
}

export function postUserPermission(user: AppUser, userId: string, permission: Permission) {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = { headers: { usid: key } };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/users/${userId}/permissions`, permission, config)
    .then(response => {
      return response.data;
    });
}

export function putUserPermission(user: AppUser, userId: string, permission: Permission) {
  const { roleSessionKey: key, activeSchoolId } = user;
  const config = { headers: { usid: key } };

  return axios
    .put(
      `${window.apiBase}/i/schools/${activeSchoolId}/users/${userId}/permissions/${permission.id}`,
      permission,
      config
    )
    .then(response => {
      return response.data;
    });
}

export function putDataToPermission(appUser: AppUser, userId: string, permissionId: string, data: any) {
  const { roleSessionKey: key, activeSchoolId } = appUser;
  const config = { headers: { usid: key } };

  return axios
    .put(`${window.apiBase}/i/schools/${activeSchoolId}/users/${userId}/permissions/${permissionId}`, data, config)
    .then(response => {
      return response.data;
    });
}

export function deleteDataToPermission(appUser: AppUser, userId: string, permissionId: string) {
  const { roleSessionKey: key, activeSchoolId } = appUser;
  const config = { headers: { usid: key } };

  return axios
    .delete(`${window.apiBase}/i/schools/${activeSchoolId}/users/${userId}/permissions/${permissionId}`, config)
    .then(response => {
      return response.data;
    });
}

export function updatePermissionsInBulk(
  appUser: AppUser,
  action: 'UPDATE' | 'REMOVE',
  operations: Array<{
    userId: string;
    permissionId: string;
    data?: { formIds?: string[]; houseIds?: string[]; isExcludedFromMisIntegration?: boolean };
  }>
) {
  const { roleSessionKey: key, activeSchoolId } = appUser;
  return axios
    .post(
      `${window.apiBase}/i/schools/${activeSchoolId}/users/permissions/bulk`,
      { action, operations },
      { headers: { usid: key } }
    )
    .then(response => response.data);
}
